import { useMemo, useRef, useState } from 'react';
import { useDrag, useDragLayer, useDrop } from 'react-dnd';
import { useTranslation } from 'react-i18next';
import { Box, Stack, useTheme } from '@mui/material';
import {
  hash,
  emptyId,
  editorUtils,
  generateTestIds,
  useEditorStore,
  useFlowBuilder2Store,
  getLayoutComponentLabel,
  ILayoutComponent,
  LayoutComponentPropertyEditorNames,
  useFlowBuilder2MoveLayoutComponentMutation,
  useFlowBuilder2AddLayoutComponentsMutation,
  useFlowBuilder2DeleteLayoutComponentMutation,
} from '@diana/core';
import { NodeHeader } from './NodeHeader';
import { DropBoxBody } from './FlowExplorer.styles';
import { ComponentNameField } from '../ComponentNameField/ComponentNameField';
import { FLOW_BUILDER2_PAGE_NS } from '../../i18n';
import { useDeleteComponent } from '../../Support/useDeleteComponent';
import { useDuplicateComponent } from '../../Support/useDuplicateComponent';
import { usePrepareComponentToAddInLayout } from '../../Support/usePrepareComponentToAddInLayout';
import { useSyncComponentNameAndDataPath } from '../../Support/useSyncComponentNameAndDataPath';
import { useFlowBuilder2PageStore } from '../../Store/useFlowBuilder2PageStore';
import { scrollToSection } from '../../Support/useBuilderScroll';

interface SectionNodeProps {
  sectionId: string;
  layoutId: string;
  taskId: string;
  stepId: string;
  level: number;
  canDrag?: boolean;
  componentIndex: number;
}

interface IDragItem {
  sectionId: string;
  layoutId: string;
  taskId: string;
  stepId: string;
}

interface IDragCollected {
  isDragging: boolean;
}

interface IDropCollected {
  isOver: boolean;
}

const DraggableSection = Symbol('section');

export const SectionNodeTestIds = generateTestIds('SectionNode', ['Root']);

export const SectionNode = (props: SectionNodeProps) => {
  const { sectionId, taskId, stepId, layoutId, componentIndex } = props;

  const { t } = useTranslation(FLOW_BUILDER2_PAGE_NS);

  const bodyRef = useRef<HTMLDivElement>(null);

  const [dropPlacement, setDropPlacement] = useState<'before' | 'after' | null>(null);

  const setActiveComponent = useFlowBuilder2PageStore((s) => s.setActiveComponent);
  const activeComponent = useFlowBuilder2PageStore((s) => s.activeComponent);

  const flowId = useEditorStore((s) => s.flow?.id ?? null);
  const getLayoutById = useEditorStore((s) => s.getLayoutById);
  const getLayoutComponentById = useEditorStore((s) => s.getLayoutComponentById);
  const updateLayoutById = useEditorStore((s) => s.updateLayoutById);
  const registerRequest = useFlowBuilder2Store((s) => s.registerRequest);
  const validationResult = useFlowBuilder2Store((s) => {
    return s.getFieldState(
      sectionId,
      'layoutComponent',
      LayoutComponentPropertyEditorNames.LabelPropertyEditor,
    ).validationResult;
  });

  const setTemporaryLayoutComponentId = useEditorStore((s) => s.setTemporaryLayoutComponentId);

  const displayName = useEditorStore((s) => {
    const layoutComponent = s.getLayoutComponentById(layoutId, sectionId);
    return layoutComponent ? getLayoutComponentLabel(layoutComponent) : '';
  });

  const { palette, spacing } = useTheme();

  const { deleteComponent, isDeletingComponent } = useDeleteComponent();
  const { duplicateComponent, isDuplicatingComponent } = useDuplicateComponent();

  const { mutateAsync: moveComponent } = useFlowBuilder2MoveLayoutComponentMutation({});
  const { mutateAsync: addComponents } = useFlowBuilder2AddLayoutComponentsMutation({});
  const { mutateAsync: deleteComponentSilently } = useFlowBuilder2DeleteLayoutComponentMutation({});

  const prepareComponentToAddInLayout = usePrepareComponentToAddInLayout({
    parentId: emptyId,
    layoutId,
    taskId,
    stepId,
    flowId,
  });

  const syncComponentNameAndDataPath = useSyncComponentNameAndDataPath({
    layoutId,
    taskId,
    stepId,
    flowId,
  });

  const isChildSelected = useMemo(() => {
    if (activeComponent) {
      const { layoutId, componentId } = activeComponent;
      const childComponent = getLayoutComponentById(layoutId, componentId);
      return childComponent?.parentId === sectionId;
    }

    return false;
  }, [activeComponent, getLayoutComponentById, sectionId]);

  const canDrag = props.canDrag;
  const isSelected = activeComponent?.componentId === sectionId || isChildSelected;

  const [{ isDragging }, drag, preview] = useDrag<IDragItem, unknown, IDragCollected>(
    {
      type: DraggableSection,
      item: { sectionId, taskId, stepId, layoutId },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    },
    [sectionId, taskId, stepId, layoutId],
  );

  const [{ isOver }, drop] = useDrop<IDragItem, unknown, IDropCollected>(
    {
      accept: DraggableSection,
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      }),
      hover: (item, monitor) => {
        if (bodyRef.current) {
          const bodyRect = bodyRef.current.getBoundingClientRect();
          const pointerOffset = monitor.getClientOffset();
          if (item.sectionId === sectionId) setDropPlacement(null);
          else if (bodyRect && pointerOffset) {
            const center = bodyRect.top + bodyRect.height / 2;
            if (pointerOffset.y <= center) setDropPlacement('before');
            else setDropPlacement('after');
          }
        }
      },
      drop: (item) => {
        const sourceLayout = getLayoutById(item.layoutId);
        const targetLayout = getLayoutById(layoutId);
        const targetIndex = targetLayout?.components.findIndex((c) => c.id === sectionId) ?? -1;

        if (sourceLayout && targetLayout && dropPlacement && targetIndex >= 0) {
          const index = dropPlacement === 'after' ? targetIndex : targetIndex - 1;

          // same layout
          if (sourceLayout.id === targetLayout.id) {
            updateLayoutById(targetLayout.id, (draft) => {
              const component = draft.components.find((c) => c.id === item.sectionId);
              if (component) {
                const descendants = editorUtils.getLayoutComponentDescendants({
                  componentId: component.id,
                  layout: draft,
                });
                const components = [component, ...descendants];
                const componentIds = components.map((c) => c.id);
                const newComponents = draft.components.filter((c) => !componentIds.includes(c.id));
                draft.components = newComponents.reduce<ILayoutComponent[]>((acc, item) => {
                  if (item.id === sectionId) {
                    if (dropPlacement === 'before') acc.push(...components, item);
                    else acc.push(item, ...components);
                  } else acc.push(item);
                  return acc;
                }, []);
              }

              return draft;
            });

            registerRequest(
              () =>
                moveComponent({
                  layoutId,
                  componentId: item.sectionId,
                  afterComponentId: targetLayout.components[index]?.id ?? emptyId,
                }).catch(console.error),
              {
                key: hash(['MOVE_LAYOUT_COMPONENT', item.sectionId]),
                objectId: item.sectionId,
                objectType: 'layoutComponent',
              },
            );
          }

          // different layout
          if (sourceLayout.id !== targetLayout.id) {
            const component = sourceLayout.components.find((c) => c.id === item.sectionId);
            if (component) {
              const descendants = editorUtils.getLayoutComponentDescendants({
                componentId: component.id,
                layout: sourceLayout,
              });

              const components = [...prepareComponentToAddInLayout(component), ...descendants].map(
                syncComponentNameAndDataPath,
              );
              const componentIds = components.map((c) => c.id);

              componentIds.forEach((id) => setTemporaryLayoutComponentId(id, true));
              updateLayoutById(sourceLayout.id, (draft) => {
                draft.components = draft.components.filter((c) => !componentIds.includes(c.id));
                return draft;
              });

              updateLayoutById(targetLayout.id, (draft) => {
                draft.components = draft.components.reduce<ILayoutComponent[]>((acc, item) => {
                  if (item.id === sectionId) {
                    if (dropPlacement === 'before') acc.push(...components, item);
                    else acc.push(item, ...components);
                  } else acc.push(item);
                  return acc;
                }, []);
                return draft;
              });

              (async () => {
                await registerRequest(
                  () =>
                    addComponents({
                      layoutId: targetLayout.id,
                      afterComponent: targetLayout.components[index]?.id ?? emptyId,
                      data: components,
                    })
                      .then(() =>
                        componentIds.forEach((id) => setTemporaryLayoutComponentId(id, false)),
                      )
                      .catch(console.error),
                  {
                    key: hash(['DND_ADD_COMPONENT', targetLayout.id, component.id]),
                    objectId: targetLayout.id,
                    objectType: 'layout',
                  },
                );

                for (const componentId of componentIds) {
                  await registerRequest(
                    () =>
                      deleteComponentSilently({
                        layoutId: sourceLayout.id,
                        componentId,
                      }),
                    {
                      key: hash(['DND_DELETE_COMPONENT', componentId]),
                      objectId: componentId,
                      objectType: 'layoutComponent',
                    },
                  );
                }
              })();
            }
          }
        }
      },
    },
    [sectionId, layoutId, getLayoutById, dropPlacement, moveComponent],
  );

  drop(bodyRef);
  preview(bodyRef);

  return (
    <Box
      data-testid={SectionNodeTestIds.Root}
      data-dragging={isDragging}
      data-section-node-id={sectionId}
      position="relative"
      sx={{ '&:first-child .tree-branch-line': { top: 0, height: 22 } }}
    >
      <Box borderRadius={1} bgcolor={isDragging ? palette.action.hover : ''}>
        <DropBoxBody
          ref={bodyRef}
          data-drop-placement={isOver ? dropPlacement : ''}
          position="relative"
          style={{ opacity: isDragging ? 0 : 1 }}
        >
          <Box ref={!!canDrag ? drag : undefined}>
            <NodeHeader
              canRename
              isSelected={isSelected}
              label={displayName}
              untitledLabel={t('labels.untitledSection')}
              actions={[
                {
                  title: t('buttons.duplicate'),
                  loading: isDuplicatingComponent,
                  execute: async () => {
                    duplicateComponent({ layoutId, componentId: sectionId }).catch();
                  },
                },
                {
                  title: t('buttons.delete'),
                  color: palette.error.main,
                  loading: isDeletingComponent,
                  execute: async () => {
                    deleteComponent({ layoutId, componentId: sectionId }).catch();
                  },
                },
              ]}
              validationResult={validationResult}
              renderNameTextField={(props) => (
                <ComponentNameField
                  layoutId={layoutId}
                  componentId={sectionId}
                  componentIndex={componentIndex}
                  multiline
                  fullWidth
                  variant="standard"
                  disableValidationResultTooltip
                  placeholder={t('labels.untitledSection')}
                  InputProps={{ sx: { p: 0 } }}
                  inputProps={{
                    sx: {
                      py: 0,
                      px: 0.5,
                      borderRadius: 1.5,
                      backgroundColor: palette.background.card,
                    },
                  }}
                  sx={{ top: 4, minHeight: spacing(3.5) }}
                  {...props}
                />
              )}
              highlightOnHover
              onClick={() => {
                setActiveComponent({ layoutId, componentId: sectionId });
                scrollToSection(sectionId);
              }}
            />
          </Box>
        </DropBoxBody>
      </Box>
    </Box>
  );
};

export const SectionDropPlaceholderTestIds = generateTestIds('SectionDropPlaceholderTestIds', [
  'Root',
]);

export const SectionDropPlaceholder = (props: {
  layoutId: string;
  taskId: string;
  stepId: string;
}) => {
  const { layoutId, taskId, stepId } = props;

  const bodyRef = useRef<HTMLDivElement>(null);

  const flowId = useEditorStore((s) => s.flow?.id ?? null);
  const getLayoutById = useEditorStore((s) => s.getLayoutById);
  const updateLayoutById = useEditorStore((s) => s.updateLayoutById);
  const setTemporaryLayoutComponentId = useEditorStore((s) => s.setTemporaryLayoutComponentId);
  const registerRequest = useFlowBuilder2Store((s) => s.registerRequest);

  const isDragging = useDragLayer(
    (monitor) => monitor.isDragging() && monitor.getItemType() === DraggableSection,
  );

  const prepareComponentToAddInLayout = usePrepareComponentToAddInLayout({
    parentId: emptyId,
    layoutId,
    taskId,
    stepId,
    flowId,
  });

  const syncComponentNameAndDataPath = useSyncComponentNameAndDataPath({
    layoutId,
    taskId,
    stepId,
    flowId,
  });

  const { mutateAsync: addComponents } = useFlowBuilder2AddLayoutComponentsMutation({});
  const { mutateAsync: deleteComponentSilently } = useFlowBuilder2DeleteLayoutComponentMutation({});

  const [{ isOver }, drop] = useDrop<IDragItem, unknown, IDropCollected>(
    {
      accept: DraggableSection,
      collect: (monitor) => ({ isOver: monitor.isOver() }),
      drop: (item) => {
        const sourceLayout = getLayoutById(item.layoutId);
        const targetLayout = getLayoutById(layoutId);

        if (sourceLayout && targetLayout) {
          if (sourceLayout.id !== targetLayout.id) {
            const component = sourceLayout.components.find((c) => c.id === item.sectionId);
            if (component) {
              const descendants = editorUtils.getLayoutComponentDescendants({
                componentId: component.id,
                layout: sourceLayout,
              });

              const components = [...prepareComponentToAddInLayout(component), ...descendants].map(
                syncComponentNameAndDataPath,
              );
              const componentIds = components.map((c) => c.id);

              componentIds.forEach((id) => setTemporaryLayoutComponentId(id, true));
              updateLayoutById(sourceLayout.id, (draft) => {
                draft.components = draft.components.filter((c) => !componentIds.includes(c.id));
                return draft;
              });

              updateLayoutById(targetLayout.id, (draft) => {
                draft.components = [...components, ...draft.components];
                return draft;
              });

              (async () => {
                await registerRequest(
                  () =>
                    addComponents({
                      layoutId: targetLayout.id,
                      afterComponent: emptyId,
                      data: components,
                    })
                      .then(() =>
                        componentIds.forEach((id) => setTemporaryLayoutComponentId(id, false)),
                      )
                      .catch(console.error),
                  {
                    key: hash(['DND_ADD_COMPONENT', targetLayout.id, component.id]),
                    objectId: targetLayout.id,
                    objectType: 'layout',
                  },
                );

                for (const componentId of componentIds) {
                  await registerRequest(
                    () =>
                      deleteComponentSilently({
                        layoutId: sourceLayout.id,
                        componentId,
                      }),
                    {
                      key: hash(['DND_DELETE_COMPONENT', componentId]),
                      objectId: componentId,
                      objectType: 'layoutComponent',
                    },
                  );
                }
              })();
            }
          }
        }
      },
    },
    [],
  );

  drop(bodyRef);

  return (
    <Box
      data-testid={SectionDropPlaceholderTestIds.Root}
      data-active={isDragging}
      data-hover={isOver}
      display={isDragging ? 'block' : 'none'}
      position="relative"
      height={0}
      sx={{ '&:not(:first-child)': { display: 'none' } }}
    >
      <Stack
        ref={bodyRef}
        alignItems="center"
        position="absolute"
        top={-8}
        left={0}
        right={0}
        height={16}
      >
        <Box
          height={2}
          width="100%"
          display={isOver ? 'block' : 'none'}
          bgcolor={({ palette }) => palette.primary.main}
        />
      </Stack>
    </Box>
  );
};
